/**
 * 🎨 Unified Background Manager
 * VideoSystemFacadeの後方互換ラッパー
 *
 * @deprecated VideoSystemFacadeを直接使用してください
 */

import { logger } from '@/utils/logger.js';
import { VideoSystemFacade, type FacadeConfig, type SystemStatus } from './unified/index.js';
import type { VideoCategory } from './VideoQueue.js';

// ============================================================================
// Type Definitions (後方互換性のため維持)
// ============================================================================

/**
 * Background manager configuration
 */
export interface BackgroundManagerConfig {
  /** 切り替え間隔（ミリ秒） */
  switchInterval: number;
  /** 動画コンテナのID */
  videoContainerId: string;
  /** トランジション有効化フラグ */
  transitionEnabled: boolean;
  /** 動画カテゴリ */
  category: VideoCategory;
}

/**
 * Gift data structure
 */
export interface GiftData {
  /** ギフト名 */
  name: string;
  /** ギフトソースパス */
  src?: string;
  /** ギフトID */
  id?: string;
  /** メタデータ */
  metadata?: Record<string, unknown>;
}

/**
 * Background manager status
 */
export interface BackgroundManagerStatus {
  /** 初期化済みフラグ */
  initialized: boolean;
  /** アクティブフラグ */
  active: boolean;
  /** 設定 */
  config?: BackgroundManagerConfig;
  /** 現在の動画情報 */
  currentVideo?: {
    src: string | null;
    type: string | null;
    element: 'A' | 'B' | null;
  };
  /** システム状態 */
  system?: {
    isTransitioning: boolean;
    giftMode: boolean;
  };
  /** キュー状態 */
  queueStatus?: {
    totalVideos: number;
  };
  /** タイマー状態 */
  timerStatus?: {
    active?: boolean;
    activeIntervals?: number;
    scheduledTimeouts?: number;
  };
}

// ============================================================================
// UnifiedBackgroundManager Class
// ============================================================================

/**
 * Unified Background Manager
 * @deprecated VideoSystemFacadeを直接使用してください
 */
class UnifiedBackgroundManager {
  private facade: VideoSystemFacade | null = null;
  private config: BackgroundManagerConfig;

  constructor() {
    this.config = {
      switchInterval: 30000,
      videoContainerId: '.character-only-panel',
      transitionEnabled: true,
      category: 'all'
    };
  }

  /**
   * 初期化
   */
  public async initialize(): Promise<boolean> {
    try {
      const facadeConfig: Partial<FacadeConfig> = {
        containerId: this.config.videoContainerId,
        switchInterval: this.config.switchInterval,
        debug: false
      };

      this.facade = VideoSystemFacade.getInstance(facadeConfig);
      await this.facade.initialize();

      logger.info('✅ UnifiedBackgroundManager initialized (using VideoSystemFacade)');
      return true;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      logger.error('❌ Failed to initialize UnifiedBackgroundManager:', errorMessage);
      return false;
    }
  }

  /**
   * 背景動画の再生を開始
   */
  public async start(): Promise<void> {
    if (!this.facade) {
      throw new Error('Not initialized. Call initialize() first.');
    }

    // 最初の背景動画を再生
    await this.facade.switchBackground();

    // スケジューラを開始
    this.facade.startScheduler();

    logger.info('✅ UnifiedBackgroundManager started');
  }

  /**
   * 背景動画の再生を停止
   */
  public stop(): void {
    if (this.facade) {
      this.facade.stopScheduler();
    }
  }

  /**
   * ギフト動画を再生
   */
  public async playGift(giftData: GiftData, duration = 8000): Promise<boolean> {
    if (!this.facade) {
      logger.error('❌ Not initialized');
      return false;
    }

    return this.facade.playGift(
      { src: giftData.src || '', name: giftData.name, category: 'all' },
      { duration }
    );
  }

  /**
   * カテゴリを設定
   */
  public setCategory(category: VideoCategory): void {
    this.config.category = category;
    // Note: Facadeの設定更新は次回initialize時に反映
  }

  /**
   * 切り替え間隔を設定
   */
  public setSwitchInterval(intervalMs: number): void {
    this.config.switchInterval = intervalMs;
    // Facadeのスケジューラを再設定
    if (this.facade) {
      this.facade.stopScheduler();
      this.facade.startScheduler();
    }
  }

  /**
   * 次の動画に手動で切り替え
   */
  public async switchToNext(): Promise<boolean> {
    if (!this.facade) {
      logger.error('❌ Not initialized');
      return false;
    }

    return this.facade.switchBackground();
  }

  /**
   * 現在の状態を取得
   */
  public getStatus(): BackgroundManagerStatus {
    if (!this.facade) {
      return {
        initialized: false,
        active: false
      };
    }

    const status: SystemStatus = this.facade.getStatus();
    const state = this.facade.getState();

    return {
      initialized: status.initialized,
      active: status.schedulerActive,
      config: this.config,
      currentVideo: status.currentVideo ? {
        src: status.currentVideo.src,
        type: status.currentVideo.type,
        element: status.currentVideo.element
      } : undefined,
      system: {
        isTransitioning: status.isTransitioning,
        giftMode: status.giftMode
      },
      queueStatus: {
        totalVideos: status.queueSize
      },
      timerStatus: {
        active: status.schedulerActive
      }
    };
  }

  /**
   * デバッグ情報を表示
   */
  public debug(): void {
    const status = this.getStatus();
    logger.info('🎨 UnifiedBackgroundManager Debug');
    logger.debug('Status:', status);
    if (this.facade) {
      logger.debug('Facade State:', this.facade.getState());
    }
  }

  /**
   * リセット
   */
  public reset(): void {
    this.stop();
    if (this.facade) {
      this.facade.reset();
    }
  }

  /**
   * クリーンアップ
   */
  public cleanup(): void {
    this.stop();
    if (this.facade) {
      this.facade.cleanup();
    }
    this.facade = null;
  }
}

// ES6 Export
export { UnifiedBackgroundManager };

// Legacy CommonJS Export
if (typeof module !== 'undefined' && module.exports) {
  module.exports = UnifiedBackgroundManager;
}
